import { useMemo } from "react";
import useQueryParams from "../../utils/hooks/useQueryParams";

const useTableParams = (tableId: string = 'default') => {
    const {setQueryParam, removeQueryParam, getQueryParam, searchParams} = useQueryParams();
    const getParamKey = (key: string) => `${tableId}_${key}`;

    const DEFAULT_SORT = 'id:asc';
    const DEFAULT_PAGE = 0;
    const DEFAULT_ROWS_PER_PAGE = 10;

    const sortKey = getParamKey('sort');
    const pageKey = getParamKey('page');
    const rowsPerPageKey = getParamKey('rowsPerPage');
    const filterKey = getParamKey('filter:');

    const sortParam = getQueryParam(sortKey) ?? DEFAULT_SORT;
    const pageParam = getQueryParam(pageKey) ?? DEFAULT_PAGE.toString();
    const rowsPerPageParam = getQueryParam(rowsPerPageKey) ?? DEFAULT_ROWS_PER_PAGE.toString();

    const [sortKeysString, sortDirectionsString] = sortParam.split(':') as [string, string];
    const sortKeys = sortKeysString.split(',');
    const sortDirections = sortDirectionsString.split(',') as ('asc' | 'desc')[];

    const page = parseInt(pageParam, 10);
    const rowsPerPage = parseInt(rowsPerPageParam, 10);

    const filters = useMemo(() => {
        const filters: Record<string, string> = {};
        searchParams.forEach((value: string, key: string) => {
            if (key.startsWith(filterKey)) {
                filters[key.slice(filterKey.length)] = value;
            }
        });
        return filters;
    }, [filterKey, searchParams]);

    const setSort = (key: string | null, direction: 'asc' | 'desc' | null) => {
        if (key === null) {
            removeQueryParam(sortKey);
            return;
        }

        let newSortKeys = [...sortKeys];
        let newSortDirections = [...sortDirections];

        // if null, remove sort key and direction
        if (direction === null) {
            const keyIndex = newSortKeys.indexOf(key);
            if (keyIndex !== -1) {
                newSortKeys.splice(keyIndex, 1);
                newSortDirections.splice(keyIndex, 1);
            }

            // if no sort keys left, remove sort query param
            if (newSortKeys.length === 0) {
                removeQueryParam(sortKey);
            } else {
                setQueryParam(sortKey, `${newSortKeys.join(',')}:${newSortDirections.join(',')}`);
            }
            return;
        }

        // if key is not in sortKeys, add it
        const keyIndex = newSortKeys.indexOf(key);
        if (keyIndex === -1) {
            // if sorting by id and adding another key, remove id
            if (newSortKeys.length === 1 && newSortKeys[0] === 'id') {
                newSortKeys = [];
                newSortDirections = [];
            }
            newSortKeys.push(key);
            newSortDirections.push(direction);
        } else {
            newSortDirections[keyIndex] = direction;
        }

        setQueryParam(sortKey, `${newSortKeys.join(',')}:${newSortDirections.join(',')}`);
    };

    const setPage = (newPage: number) => {
        if (newPage === DEFAULT_PAGE) {
            removeQueryParam(pageKey);
            return;
        }
        setQueryParam(pageKey, newPage.toString());
    };

    const setRowsPerPage = (newPerPage: number | string) => {
        if (newPerPage === DEFAULT_ROWS_PER_PAGE) {
            removeQueryParam(rowsPerPageKey);
            return;
        }
        setQueryParam(rowsPerPageKey, newPerPage.toString());
    };

    const setFilter = (column: string, value: string | number) => {
        if (value === '') {
            removeFilter(column);
            return;
        }
        setQueryParam(`${filterKey}${column}`, value);
    };

    const removeFilter = (column: string) => {
        removeQueryParam(`${filterKey}${column}`);
    };

    const clearFilters = () => {
        searchParams.forEach((_, key) => {
            if (key.startsWith(filterKey)) {
                removeQueryParam(key);
            }
        });
    }

    return {
        sortKeys,
        sortDirections,
        setSort,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        filters,
        setFilter,
        removeFilter,
        clearFilters,
    };
};

export default useTableParams;
